import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import '../styles/MarketingDashboard.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, ArcElement,
  Title, Tooltip, Legend
);

const MarketingDashboard = () => {
  // Line Chart: Performance Overview
  const perfLineData = {
    labels: ['01 May', '04 May', '07 May', '10 May', '13 May', '16 May', '20 May'],
    datasets: [
      {
        label: 'Impressions',
        data: [40000, 60000, 50000, 70000, 55000, 80000, 95000],
        borderColor: '#3b82f6',
        backgroundColor: '#3b82f6',
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.4,
      },
      {
        label: 'Clicks',
        data: [20000, 35000, 25000, 45000, 30000, 50000, 65000],
        borderColor: '#22c55e',
        backgroundColor: '#22c55e',
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.4,
      },
      {
        label: 'Leads',
        data: [10000, 20000, 15000, 25000, 18000, 30000, 40000],
        borderColor: '#a855f7',
        backgroundColor: '#a855f7',
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.4,
      },
      {
        label: 'Conversions',
        data: [5000, 10000, 8000, 15000, 10000, 18000, 25000],
        borderColor: '#f59e0b',
        backgroundColor: '#f59e0b',
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.4,
      }
    ],
  };

  const perfLineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#848a95', font: { size: 10 } } },
      y: { 
        grid: { color: '#f4f5f8' }, 
        ticks: { color: '#848a95', font: { size: 10 }, callback: (value) => value === 0 ? '0' : `${value / 1000}K` },
        max: 100000,
      },
    },
  };

  // Doughnut: Leads by Source
  const sourceDonutData = {
    labels: ['Facebook Ads', 'Google Ads', 'WhatsApp', 'Email', 'Instagram', 'Others'],
    datasets: [{
      data: [385, 287, 276, 186, 122, 0],
      backgroundColor: ['#3b82f6', '#22c55e', '#a855f7', '#f59e0b', '#ef4444', '#848a95'],
      borderWidth: 0,
      cutout: '72%',
    }],
  };

  // Doughnut: Audience Gender
  const genderDonutData = {
    labels: ['Male', 'Female', 'Other'],
    datasets: [{
      data: [62.5, 35.2, 2.3],
      backgroundColor: ['#3b82f6', '#22c55e', '#a855f7'],
      borderWidth: 0,
      cutout: '72%',
    }],
  };

  const donutOptions = { plugins: { legend: { display: false } }, maintainAspectRatio: false };

  // Tables Mock Data
  const topCampaigns = [
    { name: "Summer Sale 2025", sub: "Promoting summer offers", icon: "fa-brands fa-facebook", color: "#3b82f6", channel: "Facebook Ads", leads: 342, conv: 45, rate: "13.16%", cost: "₹12,450", roi: "512%" },
    { name: "New Customer Offer", sub: "Welcome discount campaign", icon: "fa-brands fa-google", color: "#22c55e", channel: "Google Ads", leads: 288, conv: 32, rate: "10.74%", cost: "₹8,800", roi: "435%" },
    { name: "WhatsApp Promotion", sub: "Broadcast offer campaign", icon: "fa-brands fa-whatsapp", color: "#22c55e", channel: "WhatsApp", leads: 276, conv: 28, rate: "10.14%", cost: "₹4,200", roi: "389%" },
    { name: "Email Newsletter", sub: "Monthly newsletter campaign", icon: "fa-regular fa-envelope", color: "#a855f7", channel: "Email", leads: 186, conv: 15, rate: "8.06%", cost: "₹2,900", roi: "276%" },
    { name: "Instagram Stories", sub: "Brand awareness campaign", icon: "fa-brands fa-instagram", color: "#ef4444", channel: "Instagram", leads: 154, conv: 12, rate: "7.79%", cost: "₹3,450", roi: "245%" }
  ];

  const recentCampaigns = [
    { name: "Festival Offer May", channel: "Facebook Ads", status: "Active", date: "18 May 2025", budget: "₹8,000", spent: "₹3,250" },
    { name: "Loyalty Program", channel: "Email", status: "Active", date: "15 May 2025", budget: "₹3,000", spent: "₹1,120" },
    { name: "Retargeting Ads", channel: "Google Ads", status: "Paused", date: "12 May 2025", budget: "₹6,500", spent: "₹4,850" },
    { name: "Refer & Earn", channel: "WhatsApp", status: "Completed", date: "05 May 2025", budget: "₹2,000", spent: "₹1,870" },
    { name: "Instagram Reels Promo", channel: "Instagram", status: "Active", date: "03 May 2025", budget: "₹4,000", spent: "₹2,780" }
  ];

  return (
    <div className="mkt-wrapper">
      
      {/* 1. Top KPI Grid */}
      <div className="mkt-kpi-grid">
        <div className="mkt-card mkt-kpi-card">
          <div className="mkt-icon-box mkt-bg-blue-light"><i className="fa-solid fa-bullhorn mkt-text-blue"></i></div>
          <div className="mkt-kpi-info">
            <span className="mkt-kpi-title">Total Campaigns</span>
            <h2 className="mkt-kpi-value">24</h2>
            <div className="mkt-kpi-meta"><span>All Campaigns</span><span className="mkt-trend-green"><i className="fa-solid fa-arrow-up"></i> 26.7%</span></div>
          </div>
        </div>
        <div className="mkt-card mkt-kpi-card">
          <div className="mkt-icon-box mkt-bg-green-light"><i className="fa-solid fa-users mkt-text-green"></i></div>
          <div className="mkt-kpi-info">
            <span className="mkt-kpi-title">Total Leads</span>
            <h2 className="mkt-kpi-value">1,256</h2>
            <div className="mkt-kpi-meta"><span>All Leads</span><span className="mkt-trend-green"><i className="fa-solid fa-arrow-up"></i> 18.4%</span></div>
          </div>
        </div>
        <div className="mkt-card mkt-kpi-card">
          <div className="mkt-icon-box mkt-bg-purple-light"><i className="fa-solid fa-user-plus mkt-text-purple"></i></div>
          <div className="mkt-kpi-info">
            <span className="mkt-kpi-title">New Customers</span>
            <h2 className="mkt-kpi-value">132</h2>
            <div className="mkt-kpi-meta"><span>This Period</span><span className="mkt-trend-green"><i className="fa-solid fa-arrow-up"></i> 22.5%</span></div>
          </div>
        </div>
        <div className="mkt-card mkt-kpi-card">
          <div className="mkt-icon-box mkt-bg-orange-light"><i className="fa-solid fa-chart-line mkt-text-orange"></i></div>
          <div className="mkt-kpi-info">
            <span className="mkt-kpi-title">Conversion Rate</span>
            <h2 className="mkt-kpi-value">10.52%</h2>
            <div className="mkt-kpi-meta"><span>Avg. Conversion</span><span className="mkt-trend-green"><i className="fa-solid fa-arrow-up"></i> 2.8%</span></div>
          </div>
        </div>
        <div className="mkt-card mkt-kpi-card">
          <div className="mkt-icon-box mkt-bg-red-light"><i className="fa-solid fa-wallet mkt-text-red"></i></div>
          <div className="mkt-kpi-info">
            <span className="mkt-kpi-title">Marketing Spend</span>
            <h2 className="mkt-kpi-value">₹48,250</h2>
            <div className="mkt-kpi-meta"><span>This Period</span><span className="mkt-trend-red"><i className="fa-solid fa-arrow-down"></i> 8.6%</span></div>
          </div>
        </div>
        <div className="mkt-card mkt-kpi-card">
          <div className="mkt-icon-box mkt-bg-teal-light"><i className="fa-solid fa-bullseye mkt-text-teal"></i></div>
          <div className="mkt-kpi-info">
            <span className="mkt-kpi-title">ROI</span>
            <h2 className="mkt-kpi-value">412%</h2>
            <div className="mkt-kpi-meta"><span>Return on Investment</span><span className="mkt-trend-green"><i className="fa-solid fa-arrow-up"></i> 15.3%</span></div>
          </div>
        </div>
      </div>

      {/* 2. Middle Grid */}
      <div className="mkt-middle-grid">
        
        {/* Performance Overview Line */}
        <div className="mkt-card mkt-p-24 mkt-flex-col">
          <div className="mkt-card-header">
            <h3 className="mkt-card-title">Performance Overview</h3>
            <div className="mkt-dropdown">This Month <i className="fa-solid fa-chevron-down"></i></div>
          </div>
          
          <div className="mkt-perf-metrics">
            <div className="mkt-pm-item">
              <i className="fa-regular fa-eye mkt-text-blue"></i>
              <div><span>Impressions</span><strong>98,452</strong></div>
            </div>
            <div className="mkt-pm-item">
              <i className="fa-solid fa-hand-pointer mkt-text-green"></i>
              <div><span>Clicks</span><strong>12,845</strong></div>
            </div>
            <div className="mkt-pm-item">
              <i className="fa-solid fa-users mkt-text-purple"></i>
              <div><span>Leads</span><strong>1,256</strong></div>
            </div>
            <div className="mkt-pm-item">
              <i className="fa-solid fa-circle-check mkt-text-orange"></i>
              <div><span>Conversions</span><strong>132</strong></div>
            </div>
          </div>

          <div className="mkt-custom-legend">
            <div className="mkt-leg-h"><span className="mkt-dot mkt-bg-blue"></span> Impressions</div>
            <div className="mkt-leg-h"><span className="mkt-dot mkt-bg-green"></span> Clicks</div>
            <div className="mkt-leg-h"><span className="mkt-dot mkt-bg-purple"></span> Leads</div>
            <div className="mkt-leg-h"><span className="mkt-dot mkt-bg-orange"></span> Conversions</div>
          </div>
          
          <div className="mkt-chart-container"><Line data={perfLineData} options={perfLineOptions} /></div>
        </div>

        {/* Top Campaigns Table */}
        <div className="mkt-card mkt-p-24 mkt-flex-col">
          <div className="mkt-card-header">
            <h3 className="mkt-card-title">Top Campaigns</h3>
            <a href="#" className="mkt-link">View All</a>
          </div>
          <div className="mkt-table-wrapper" style={{flex: 1}}>
            <table className="mkt-table">
              <thead>
                <tr>
                  <th>Campaign Name</th><th>Channel</th><th className="mkt-align-right">Leads</th>
                  <th className="mkt-align-right">Conversions</th><th className="mkt-align-right">Conversion Rate</th>
                  <th className="mkt-align-right">Cost</th><th className="mkt-align-right">ROI</th>
                </tr>
              </thead>
              <tbody>
                {topCampaigns.map((c, i) => (
                  <tr key={i}>
                    <td>
                      <div className="mkt-campaign-cell">
                        <div className="mkt-camp-icon" style={{color: c.color}}><i className={c.icon}></i></div>
                        <div><div className="mkt-fw-600">{c.name}</div><div className="mkt-text-xs mkt-text-muted">{c.sub}</div></div>
                      </div>
                    </td>
                    <td>{c.channel}</td>
                    <td className="mkt-align-right">{c.leads}</td>
                    <td className="mkt-align-right">{c.conv}</td>
                    <td className="mkt-align-right">{c.rate}</td>
                    <td className="mkt-align-right">{c.cost}</td>
                    <td className="mkt-align-right mkt-text-green mkt-fw-600">{c.roi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Leads by Source Donut */}
        <div className="mkt-card mkt-p-24 mkt-flex-col">
          <div className="mkt-card-header">
            <h3 className="mkt-card-title">Leads by Source</h3>
            <div className="mkt-dropdown">This Month <i className="fa-solid fa-chevron-down"></i></div>
          </div>
          <div className="mkt-donut-centered">
            <div className="mkt-donut-wrapper">
              <Doughnut data={sourceDonutData} options={donutOptions} />
              <div className="mkt-donut-center">
                <span className="mkt-dc-label">Total</span>
                <strong className="mkt-dc-value">1,256</strong>
              </div>
            </div>
            <div className="mkt-donut-legend mkt-mt-16">
              <div className="mkt-leg-item"><span className="mkt-dot mkt-bg-blue"></span><div><strong>Facebook Ads</strong><span>385 (30.6%)</span></div></div>
              <div className="mkt-leg-item"><span className="mkt-dot mkt-bg-green"></span><div><strong>Google Ads</strong><span>287 (22.8%)</span></div></div>
              <div className="mkt-leg-item"><span className="mkt-dot mkt-bg-purple"></span><div><strong>WhatsApp</strong><span>276 (21.9%)</span></div></div>
              <div className="mkt-leg-item"><span className="mkt-dot mkt-bg-orange"></span><div><strong>Email</strong><span>186 (14.8%)</span></div></div>
              <div className="mkt-leg-item"><span className="mkt-dot mkt-bg-red"></span><div><strong>Instagram</strong><span>122 (9.7%)</span></div></div>
              <div className="mkt-leg-item"><span className="mkt-dot mkt-bg-gray"></span><div><strong>Others</strong><span>(0.2%)</span></div></div>
            </div>
          </div>
          <div className="mkt-mt-auto"><a href="#" className="mkt-link">View full report <i className="fa-solid fa-arrow-right"></i></a></div>
        </div>

      </div>

      {/* 3. Bottom Grid */}
      <div className="mkt-bottom-grid">
        
        {/* Audience Overview */}
        <div className="mkt-card mkt-p-24 mkt-flex-col">
          <h3 className="mkt-card-title mkt-mb-20">Audience Overview</h3>
          
          <div className="mkt-audience-split">
            {/* Gender Donut */}
            <div className="mkt-aud-left">
              <span className="mkt-aud-title">Gender</span>
              <div className="mkt-donut-wrapper" style={{width: '120px', height: '120px', margin: '16px auto'}}>
                <Doughnut data={genderDonutData} options={donutOptions} />
                <div className="mkt-donut-center">
                  <span className="mkt-dc-label" style={{fontSize:'10px'}}>Total</span>
                  <strong className="mkt-dc-value" style={{fontSize:'14px'}}>1,256</strong>
                </div>
              </div>
              <div className="mkt-donut-legend" style={{gap: '8px'}}>
                <div className="mkt-leg-item"><span className="mkt-dot mkt-bg-blue"></span><div className="mkt-aud-leg"><span>Male</span><strong>62.5%</strong></div></div>
                <div className="mkt-leg-item"><span className="mkt-dot mkt-bg-green"></span><div className="mkt-aud-leg"><span>Female</span><strong>35.2%</strong></div></div>
                <div className="mkt-leg-item"><span className="mkt-dot mkt-bg-purple"></span><div className="mkt-aud-leg"><span>Other</span><strong>2.3%</strong></div></div>
              </div>
            </div>

            {/* Age Group Horizontal Bars */}
            <div className="mkt-aud-right">
              <span className="mkt-aud-title">Age Group</span>
              <div className="mkt-age-list mkt-mt-16">
                <div className="mkt-hb-row"><span>18-24</span><div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-blue" style={{width:'18.6%'}}></div></div><strong>18.6%</strong></div>
                <div className="mkt-hb-row"><span>25-34</span><div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-blue" style={{width:'41.3%'}}></div></div><strong>41.3%</strong></div>
                <div className="mkt-hb-row"><span>35-44</span><div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-blue" style={{width:'24.7%'}}></div></div><strong>24.7%</strong></div>
                <div className="mkt-hb-row"><span>45-54</span><div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-blue" style={{width:'10.2%'}}></div></div><strong>10.2%</strong></div>
                <div className="mkt-hb-row"><span>55+</span><div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-blue" style={{width:'5.2%'}}></div></div><strong>5.2%</strong></div>
              </div>
            </div>
          </div>

          <div className="mkt-alert-box mkt-mt-auto">
            <i className="fa-solid fa-circle-info mkt-text-blue"></i>
            <div>
              <strong>25-34 age group is your most active audience.</strong>
              <span>Consider creating more targeted campaigns for this segment.</span>
            </div>
          </div>
        </div>

        {/* Recent Campaigns Table */}
        <div className="mkt-card mkt-p-24 mkt-flex-col">
          <div className="mkt-card-header">
            <h3 className="mkt-card-title">Recent Campaigns</h3>
            <a href="#" className="mkt-link">View All</a>
          </div>
          <div className="mkt-table-wrapper" style={{flex: 1}}>
            <table className="mkt-table">
              <thead>
                <tr><th>Campaign</th><th>Channel</th><th>Status</th><th>Start Date</th><th>Budget</th><th>Spent</th><th></th></tr>
              </thead>
              <tbody>
                {recentCampaigns.map((c, i) => (
                  <tr key={i}>
                    <td className="mkt-fw-600">{c.name}</td>
                    <td className="mkt-text-muted">{c.channel}</td>
                    <td><span className={`mkt-badge mkt-badge-${c.status.toLowerCase()}`}>{c.status}</span></td>
                    <td>{c.date}</td>
                    <td>{c.budget}</td>
                    <td className="mkt-fw-600">{c.spent}</td>
                    <td><button className="mkt-action-btn"><i className="fa-solid fa-ellipsis-vertical"></i></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mkt-mt-16"><a href="#" className="mkt-link">View all campaigns <i className="fa-solid fa-arrow-right"></i></a></div>
        </div>

        {/* Right Col Wrapper (Channel Perf & Quick Actions) */}
        <div className="mkt-right-col-stack">
          
          {/* Channel Performance */}
          <div className="mkt-card mkt-p-24 mkt-flex-col">
            <div className="mkt-card-header">
              <h3 className="mkt-card-title">Channel Performance</h3>
              <div className="mkt-dropdown">This Month <i className="fa-solid fa-chevron-down"></i></div>
            </div>
            <div className="mkt-channel-list">
              <div className="mkt-ch-row"><i className="fa-brands fa-facebook mkt-text-blue"></i> <span className="mkt-ch-name">Facebook Ads</span> <div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-blue" style={{width:'38.3%'}}></div></div> <div className="mkt-ch-val"><strong>₹18,450</strong> (38.3%)</div></div>
              <div className="mkt-ch-row"><i className="fa-brands fa-google mkt-text-green"></i> <span className="mkt-ch-name">Google Ads</span> <div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-green" style={{width:'30.4%'}}></div></div> <div className="mkt-ch-val"><strong>₹14,650</strong> (30.4%)</div></div>
              <div className="mkt-ch-row"><i className="fa-brands fa-whatsapp mkt-text-green"></i> <span className="mkt-ch-name">WhatsApp</span> <div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-green" style={{width:'12.8%'}}></div></div> <div className="mkt-ch-val"><strong>₹6,200</strong> (12.8%)</div></div>
              <div className="mkt-ch-row"><i className="fa-regular fa-envelope mkt-text-purple"></i> <span className="mkt-ch-name">Email</span> <div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-purple" style={{width:'8.6%'}}></div></div> <div className="mkt-ch-val"><strong>₹4,150</strong> (8.6%)</div></div>
              <div className="mkt-ch-row"><i className="fa-brands fa-instagram mkt-text-red"></i> <span className="mkt-ch-name">Instagram</span> <div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-red" style={{width:'8.6%'}}></div></div> <div className="mkt-ch-val"><strong>₹4,150</strong> (8.6%)</div></div>
              <div className="mkt-ch-row"><i className="fa-solid fa-circle-dot mkt-text-gray"></i> <span className="mkt-ch-name">Others</span> <div className="mkt-hb-track"><div className="mkt-hb-fill mkt-bg-gray" style={{width:'1.3%'}}></div></div> <div className="mkt-ch-val"><strong>₹650</strong> (1.3%)</div></div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mkt-card mkt-p-24 mkt-flex-col">
            <h3 className="mkt-card-title mkt-mb-16">Quick Actions</h3>
            <div className="mkt-qa-grid">
              <div className="mkt-qa-item">
                <div className="mkt-icon-sm mkt-bg-blue-light mkt-text-blue"><i className="fa-solid fa-bullhorn"></i></div>
                <div className="mkt-qa-text"><strong>Create Campaign</strong><span>Launch new campaign</span></div>
              </div>
              <div className="mkt-qa-item">
                <div className="mkt-icon-sm mkt-bg-blue-light mkt-text-blue"><i className="fa-solid fa-users-viewfinder"></i></div>
                <div className="mkt-qa-text"><strong>Add Audience</strong><span>Create new audience</span></div>
              </div>
              <div className="mkt-qa-item">
                <div className="mkt-icon-sm mkt-bg-blue-light mkt-text-blue"><i className="fa-solid fa-envelope-open-text"></i></div>
                <div className="mkt-qa-text"><strong>Email Campaign</strong><span>Send email</span></div>
              </div>
              <div className="mkt-qa-item">
                <div className="mkt-icon-sm mkt-bg-orange-light mkt-text-orange"><i className="fa-regular fa-file-image"></i></div>
                <div className="mkt-qa-text"><strong>View Templates</strong><span>Use pre-built templates</span></div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default MarketingDashboard;